import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import { useTranslation } from 'react-i18next';
import PostComponent from './PostComponent';
import SearchComponent, { nationLabel, sameNation } from '../search/SearchComponent';
import usePosts from '../../common/hooks/usePosts';
import useAuth from '../../common/hooks/useAuth';
import { REMOTE_NATION, isRemoteNation, useCountries, useNations } from '../../common/hooks/useReferenceData';
import { getLoggedInUserId } from '../../services/AuthService';
import { regionName, sortByField } from '../../utils/utils';
import { brand } from '../../theme';
import { SITE_URL } from '../../common/site';

const STORAGE_KEY = 'jobFilters';

const DEFAULT_FILTERS = {
  text: '',
  nation: null,
  country: null,
  seek: false,
  withDescription: false,
  order: 'most_recent',
  page: 1,
};

const readStoredFilters = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    // Supports the old format too (currentText, currentNation...).
    return {
      text: parsed.text ?? parsed.currentText ?? '',
      nation: parsed.nation ?? parsed.currentNation ?? null,
      country: parsed.country ?? parsed.currentCountry ?? null,
      seek: Boolean(parsed.seek ?? parsed.switchChecked),
      withDescription: Boolean(parsed.withDescription ?? parsed.switchCheckedWithoutDescription),
      order: parsed.order ?? parsed.fieldOrdered?.value ?? 'most_recent',
      page: Number(parsed.page ?? parsed.currentPage ?? 1) || 1,
    };
  } catch {
    return null;
  }
};

const formatSlug = (slug) => (slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '');

export default function ListPostsComponent({ isRemoteOnly }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { nationTag, roleSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuth } = useAuth();
  const nations = useNations();
  const allCountries = useCountries();
  const resultsTopRef = useRef(null);

  const isPseoActive = Boolean(nationTag || roleSlug || isRemoteOnly);

  // Initial filters: SEO landing URL > state passed by another page (AI assistant) > last search in this tab.
  const initialFilters = useMemo(() => {
    if (isPseoActive) {
      return {
        ...DEFAULT_FILTERS,
        text: formatSlug(roleSlug),
        nation: isRemoteOnly
          ? REMOTE_NATION
          : nationTag
            ? { nationTag: nationTag.toUpperCase(), nationName: regionName(nationTag, 'en', nationTag.toUpperCase()) }
            : null,
      };
    }
    const st = location.state;
    if (st && (st.textSearch !== undefined || st.nation !== undefined || st.isRemote)) {
      return {
        ...DEFAULT_FILTERS,
        text: st.textSearch || '',
        nation: st.isRemote ? REMOTE_NATION : st.nation || null,
        seek: Boolean(st.switchChecked),
      };
    }
    return readStoredFilters() || DEFAULT_FILTERS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationTag, roleSlug, isRemoteOnly, location.key]);

  const [filters, setFilters] = useState(initialFilters);
  const [draftText, setDraftText] = useState(initialFilters.text);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // New navigation (SEO link in the footer, AI assistant...) replaces the current filters.
  useEffect(() => {
    setFilters(initialFilters);
    setDraftText(initialFilters.text);
  }, [initialFilters]);

  // Once nations are loaded, replace the placeholder nation built from the URL with the real one.
  useEffect(() => {
    if (!filters.nation || isRemoteNation(filters.nation) || filters.nation.nationId || nations.length === 0) return;
    const match = nations.find((n) => sameNation(n, filters.nation));
    if (match) setFilters((f) => ({ ...f, nation: match }));
  }, [nations, filters.nation]);

  useEffect(() => {
    if (isPseoActive) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch { /* storage unavailable */ }
  }, [filters, isPseoActive]);

  const { posts, totalPages, totalElements, loading, error, updatePost } = usePosts({
    seek: filters.seek,
    page: filters.page,
    text: filters.text,
    nation: filters.nation,
    country: filters.country,
    withDescription: filters.withDescription,
    orderKey: filters.order,
    refreshKey,
  });

  // A remembered page (or a page chosen before the filters shrank the results) can be out of
  // range: jump back to the last existing page instead of showing an empty list.
  useEffect(() => {
    if (!loading && !error && totalPages > 0 && filters.page > totalPages) {
      setFilters((f) => ({ ...f, page: totalPages }));
    }
  }, [loading, error, totalPages, filters.page]);

  const countries = useMemo(() => {
    if (!filters.nation?.nationId || isRemoteNation(filters.nation)) return [];
    return sortByField(allCountries.filter((c) => String(c.nationId) === String(filters.nation.nationId)), 'countryName');
  }, [allCountries, filters.nation]);

  const updateFilters = useCallback((partial) => {
    setFilters((prev) => ({ ...prev, ...partial, page: partial.page ?? 1 }));
  }, []);

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    // Hide the keyboard on phones after searching.
    if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur();
    updateFilters({ text: draftText.trim() });
  };

  const handleReset = () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setDraftText('');
    if (isPseoActive) {
      navigate('/posts', { replace: false });
    }
    setFilters(DEFAULT_FILTERS);
  };

  const handlePageChange = (event, value) => {
    setFilters((prev) => ({ ...prev, page: value }));
    const top = resultsTopRef.current;
    if (top) {
      const y = top.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  };

  const handleFavouriteChange = (postId, isFav) => {
    const uid = String(getLoggedInUserId());
    updatePost(postId, (p) => {
      const current = (p.favouriteUsers || []).map(String).filter((id) => id !== uid);
      return { favouriteUsers: isFav ? [...current, uid] : current };
    });
  };

  const activeCount = [filters.text, filters.nation, filters.country, filters.seek, filters.withDescription].filter(Boolean).length;

  const activeChips = [
    filters.text && { key: 'text', label: `“${filters.text}”`, onDelete: () => { setDraftText(''); updateFilters({ text: '' }); } },
    filters.nation && { key: 'nation', label: nationLabel(filters.nation, lang, t), onDelete: () => updateFilters({ nation: null, country: null }) },
    filters.country && { key: 'country', label: filters.country.countryName, onDelete: () => updateFilters({ country: null }) },
    filters.seek && { key: 'seek', label: t('jobs.seek'), onDelete: () => updateFilters({ seek: false }) },
    filters.withDescription && { key: 'desc', label: t('jobs.alsoDescription'), onDelete: () => updateFilters({ withDescription: false }) },
  ].filter(Boolean);

  const orderOptions = [
    { value: 'most_recent', label: t('jobs.mostRecent') },
    { value: 'oldest_first', label: t('jobs.oldestFirst') },
    ...(filters.seek
      ? []
      : [
          { value: 'highest_salary', label: t('jobs.highestSalary') },
          { value: 'lowest_salary', label: t('jobs.lowestSalary') },
          { value: 'highest_roles_available', label: t('jobs.highestRoles') },
          { value: 'lowest_roles_available', label: t('jobs.lowestRoles') },
        ]),
  ];
  const orderValue = orderOptions.some((o) => o.value === filters.order) ? filters.order : 'most_recent';

  // SEO texts stay in English (indexed pages), the visible heading is translated.
  const formattedRole = formatSlug(roleSlug);
  const formattedNation = nationTag ? regionName(nationTag, 'en', nationTag.toUpperCase()) : '';
  let seoTitle = 'Find Your Dream Job Worldwide';
  let seoDescription = 'Browse thousands of job openings, remote roles, and career opportunities globally on PlaceYourJobs.';
  if (isRemoteOnly) {
    seoTitle = 'Remote Jobs Worldwide - Work From Anywhere';
    seoDescription = 'Explore top remote job opportunities worldwide. Work from home or anywhere on PlaceYourJobs.';
  } else if (formattedRole && formattedNation) {
    seoTitle = `${formattedRole} Jobs in ${formattedNation}`;
    seoDescription = `Apply for ${formattedRole} job openings in ${formattedNation}. Find global career opportunities on PlaceYourJobs.`;
  } else if (formattedNation) {
    seoTitle = `Jobs in ${formattedNation} - Search Careers`;
    seoDescription = `Discover full-time, part-time, and remote job vacancies in ${formattedNation} on PlaceYourJobs.`;
  } else if (formattedRole) {
    seoTitle = `${formattedRole} Jobs Worldwide`;
    seoDescription = `Find ${formattedRole} job vacancies and hiring opportunities around the globe on PlaceYourJobs.`;
  }

  const localNation = nationTag ? regionName(nationTag, lang, nationTag.toUpperCase()) : '';
  let heading = filters.seek ? t('jobs.seekTitle') : t('jobs.title');
  if (isPseoActive) {
    if (isRemoteOnly) heading = t('jobs.remoteJobs');
    else if (formattedRole && localNation) heading = t('jobs.roleInPlace', { role: formattedRole, place: localNation });
    else if (localNation) heading = t('jobs.jobsInPlace', { place: localNation });
    else if (formattedRole) heading = t('jobs.roleJobs', { role: formattedRole });
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Jobs', item: `${SITE_URL}/jobs` },
      ...(isPseoActive ? [{ '@type': 'ListItem', position: 3, name: seoTitle, item: `${SITE_URL}${location.pathname}` }] : []),
    ],
  };

  const filtersPanel = (
    <SearchComponent
      nations={nations}
      countries={countries}
      value={filters}
      onChange={updateFilters}
      onReset={handleReset}
      activeCount={activeCount}
      showDescriptionToggle={Boolean(filters.text)}
    />
  );

  const createJobCard = (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 5, background: brand.softGradient, borderColor: 'divider' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>{t('jobs.postAJob')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('jobs.postAJobHint')}</Typography>
      <Button
        component={Link}
        to={isAuth ? '/add-post' : '/login'}
        state={isAuth ? undefined : { from: '/add-post' }}
        variant="contained"
        fullWidth
        startIcon={<AddRoundedIcon />}
      >
        {isAuth ? t('jobs.createJob') : t('jobs.loginToCreateJobTooltip')}
      </Button>
    </Paper>
  );

  return (
    <Box sx={{ pb: { xs: 4, md: 8 } }}>
      <Helmet>
        <title>{`${seoTitle} | PlaceYourJobs`}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <link rel="canonical" href={`${SITE_URL}${location.pathname}`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Search header */}
      <Box sx={{ background: brand.softGradient, borderBottom: `1px solid ${brand.border}`, pt: { xs: 2.5, md: 5 }, pb: { xs: 2.5, md: 4 } }}>
        <Container maxWidth="lg">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 1.5, display: { xs: 'none', sm: 'flex' }, '& a, & p': { fontSize: '0.85rem' } }}>
            <MuiLink component={Link} to="/" underline="hover" color="inherit">{t('nav.home')}</MuiLink>
            <MuiLink component={Link} to="/jobs" underline="hover" color="inherit">{t('nav.jobs')}</MuiLink>
            {isPseoActive && <Typography color="text.primary" fontWeight={700}>{heading}</Typography>}
          </Breadcrumbs>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', sm: '2.1rem', md: '2.5rem' }, mb: { xs: 2, md: 3 } }}>
            {heading}
          </Typography>

          <Paper
            component="form"
            onSubmit={handleSearchSubmit}
            elevation={0}
            role="search"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 0.75,
              pl: { xs: 1.5, sm: 2 },
              borderRadius: 4,
              border: `1px solid ${brand.border}`,
              boxShadow: '0 12px 32px -18px rgba(15,23,42,0.35)',
            }}
          >
            <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
            <TextField
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              placeholder={t('home.searchPlaceholder')}
              variant="standard"
              fullWidth
              InputProps={{
                disableUnderline: true,
                endAdornment: draftText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label={t('common.clear')} onClick={() => { setDraftText(''); if (filters.text) updateFilters({ text: '' }); }}>
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              inputProps={{ 'aria-label': t('jobs.jobOrKeyword'), enterKeyHint: 'search', type: 'search' }}
              sx={{ '& input': { py: 1.2, fontSize: '1rem' }, '& input::-webkit-search-cancel-button': { display: 'none' } }}
            />
            {!isDesktop && (
              <IconButton onClick={() => setFiltersOpen(true)} aria-label={t('jobs.filters')} sx={{ border: `1px solid ${brand.border}`, borderRadius: 3 }}>
                <Badge badgeContent={activeCount - (filters.text ? 1 : 0)} color="primary">
                  <TuneRoundedIcon />
                </Badge>
              </IconButton>
            )}
            <Button type="submit" variant="contained" sx={{ px: { xs: 2, sm: 3 }, flexShrink: 0 }}>
              {isSmall ? <SearchRoundedIcon /> : t('jobs.searchJob')}
            </Button>
          </Paper>

          {activeChips.length > 0 && (
            <Stack direction="row" sx={{ mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
              {activeChips.map((chip) => (
                <Chip key={chip.key} label={chip.label} onDelete={chip.onDelete} sx={{ backgroundColor: '#FFF', border: `1px solid ${brand.border}` }} />
              ))}
              <Chip label={t('jobs.clearAll')} onClick={handleReset} variant="outlined" sx={{ borderStyle: 'dashed' }} />
            </Stack>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 } }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {isDesktop && (
            <Grid item md={3.6} lg={3.3}>
              <Stack spacing={3} sx={{ position: 'sticky', top: 'calc(var(--app-header-height) + 24px)' }}>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 5, borderColor: 'divider' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                    <TuneRoundedIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('jobs.filters')}</Typography>
                  </Stack>
                  {filtersPanel}
                </Paper>
                {createJobCard}
              </Stack>
            </Grid>
          )}

          <Grid item xs={12} md={8.4} lg={8.7}>
            <Stack
              ref={resultsTopRef}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2, gap: 1.5, flexWrap: 'wrap' }}
            >
              <Typography variant="body1" sx={{ fontWeight: 700 }} aria-live="polite">
                {loading ? t('jobs.loading') : t('jobs.resultsCount', { count: totalElements })}
              </Typography>
              <TextField
                select
                size="small"
                value={orderValue}
                onChange={(e) => updateFilters({ order: e.target.value })}
                aria-label={t('jobs.sortBy')}
                InputProps={{ startAdornment: <InputAdornment position="start"><SortRoundedIcon fontSize="small" /></InputAdornment> }}
                sx={{ minWidth: { xs: 0, sm: 230 }, flex: { xs: '1 1 180px', sm: '0 0 auto' }, '& .MuiOutlinedInput-root': { backgroundColor: '#FFF' } }}
              >
                {orderOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Stack>

            {error && !loading && (
              <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => setRefreshKey((k) => k + 1)}>{t('common.retry')}</Button>}>
                {t('common.loadError')}
              </Alert>
            )}

            {loading ? (
              <Stack spacing={2}>
                {[0, 1, 2, 3].map((i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2.5, borderRadius: 5, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={2}>
                      <Skeleton variant="circular" width={46} height={46} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="30%" />
                        <Skeleton width="80%" height={28} />
                        <Skeleton width="45%" />
                      </Box>
                    </Stack>
                    <Skeleton sx={{ mt: 1.5 }} />
                    <Skeleton width="70%" />
                  </Paper>
                ))}
              </Stack>
            ) : posts.length === 0 && !error ? (
              <Paper variant="outlined" sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center', borderRadius: 5, borderStyle: 'dashed', borderColor: 'divider' }}>
                <SearchOffRoundedIcon sx={{ fontSize: 56, color: brand.muted, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>{t('jobs.noResultsTitle')}</Typography>
                <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>{t('jobs.noJobsFound')}</Typography>
                {activeCount > 0 && <Button variant="outlined" onClick={handleReset}>{t('jobs.resetFilter')}</Button>}
              </Paper>
            ) : (
              <Stack spacing={2}>
                {posts.map((post, idx) => (
                  <Box key={post.id} sx={{ animation: `slideUpFade .45s ease-out ${Math.min(idx, 6) * 0.05}s both` }}>
                    <PostComponent
                      post={post}
                      seek={filters.seek}
                      mode="browse"
                      jobCategory="jobs"
                      countries={allCountries}
                      onFavouriteChange={handleFavouriteChange}
                    />
                  </Box>
                ))}
              </Stack>
            )}

            {!loading && posts.length > 0 && totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={Math.min(filters.page, totalPages)}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size={isSmall ? 'medium' : 'large'}
                  siblingCount={isSmall ? 0 : 1}
                  boundaryCount={1}
                />
              </Box>
            )}

            {!isDesktop && <Box sx={{ mt: 4 }}>{createJobCard}</Box>}
          </Grid>
        </Grid>
      </Container>

      {/* Mobile filters: bottom sheet */}
      <Drawer
        anchor="bottom"
        open={filtersOpen && !isDesktop}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '88vh',
            pb: 'calc(16px + var(--safe-bottom))',
          },
        }}
      >
        <Box sx={{ width: 40, height: 4, borderRadius: 2, backgroundColor: brand.border, mx: 'auto', mt: 1.25 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, pt: 1.5, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('jobs.filters')}</Typography>
          <IconButton onClick={() => setFiltersOpen(false)} aria-label={t('common.close')}><CloseRoundedIcon /></IconButton>
        </Stack>
        <Box sx={{ px: 2.5, pb: 2, overflowY: 'auto' }}>{filtersPanel}</Box>
        <Box sx={{ px: 2.5 }}>
          <Button variant="contained" fullWidth size="large" onClick={() => setFiltersOpen(false)}>
            {loading ? t('jobs.loading') : t('jobs.showResults', { count: totalElements })}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
